import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';

import { api } from '../../../../utils/apiRequest';
import {
  getValueFromLocalStorage,
  setValueInLocalStorage,
  validateValueWithRegex,
} from '../../../../utils/common';
import { integerRegex } from '../../../../utils/regexValue';

import { Button, Loader, Text, toasts } from '../../../../ui-component';
import { RightArrowIcon } from '../../../../assets/icons';
import { updateBoutiqueData } from '../../../profile/reducer';

import { updateUserData } from '../..//reducer';

import { LoginContainer, ScreenContainer } from '../../style';
import { OTPBox, OTPBoxContainer, OTPTimerText } from './style';

type OTPScreenProps = {
  dialCode: string;
  phoneNumber: string;
  setScreenType: (type: 'enterMoblie' | 'enterOTP') => void;
};

const OTPScreen = (props: OTPScreenProps) => {
  const otpNumCount = 6;
  const maxOtpTimer = 45;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { dialCode, phoneNumber } = props;

  const [isLoading, setIsLoading] = useState(false);

  const [otp, setOtp] = useState<string[]>(Array(otpNumCount).fill(''));
  const [otpTimer, setOtpTimer] = useState<number>(maxOtpTimer);
  const [errMsg, setErrMsg] = useState('');

  const inputRefs = useRef<HTMLInputElement[] | null>([]);

  useEffect(() => {
    if (otpTimer > 0) {
      const timerID = setInterval(() => setOtpTimer((otpTimer) => otpTimer - 1), 1000);

      return () => clearInterval(timerID);
    }
  }, [otpTimer]);

  const addToRefs = (el: HTMLInputElement) => {
    if (el && inputRefs.current && !inputRefs.current.includes(el)) {
      inputRefs.current.push(el);
    }
  };

  const onFocus = (e: ChangeEvent<HTMLInputElement>) => {
    e.target.removeAttribute('readOnly');
  };

  const focusNext = (index: number) => {
    if (index < otpNumCount - 1 && inputRefs.current) {
      inputRefs.current[index + 1].focus();
    }
  };

  const focusPrevious = (index: number) => {
    if (inputRefs.current && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleOtpInput = (value: string, index: number) => {
    const newOtp = otp.map((number, i) => (i === index ? value : number));
    setOtp(newOtp);
  };

  const handleChange = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    handleOtpInput(value, index);

    const { valid, message } = validateValueWithRegex(value, integerRegex);

    if (valid && index !== otpNumCount - 1) {
      focusNext(index);
      setErrMsg('');
    } else if (!valid) {
      setErrMsg(message);
    } else {
      setErrMsg('');
    }
  };

  const handleKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && index !== 0) {
      if (_isEmpty(otp[index])) {
        focusPrevious(index);
      } else {
        handleOtpInput('', index);
        setErrMsg('');
      }
    }
  };

  const handleVerifyOtp = async () => {
    try {
      const otpValue = otp.join('');

      if (otpValue.length !== otpNumCount) {
        setErrMsg(`Please enter ${otpNumCount}-digit OTP`);

        return;
      }

      setIsLoading(true);

      // 1️⃣ Verify OTP with your backend
      const verifyResponse = await api.postRequest('api/v1/auth/user/verify_otp', {
        mobile_number: `${dialCode}${phoneNumber}`, // e.g. "+919090909090"
        otp: otpValue,
      });

      const { data: verifyData, status: verifyStatus } = verifyResponse || {};

      if (!verifyStatus || !verifyData?.auth_token) {
        toasts('error', 'Invalid OTP', 'verify-otp');
        setIsLoading(false);

        return;
      }

      // 2️⃣ Save tokens in localStorage (replace old Firebase token)
      setValueInLocalStorage('userToken', verifyData.auth_token);
      setValueInLocalStorage('token', verifyData.auth_token);
      setValueInLocalStorage('refreshToken', verifyData.refresh_token);
      setValueInLocalStorage('isAuthenticated', true);

      // 3️⃣ Existing app logic: call tailor/login as before
      const loginResponse = await api.postRequest('tailor/login', {
        phone_number: phoneNumber,
        dial_code: dialCode,
      });

      const { data: loginData, status: loginStatus } = loginResponse || {};

      // Navigate to dashboard only if we have valid boutique_id (> 0)
      if (
        loginStatus &&
        !_isNil(loginData) &&
        !_isEmpty(loginData) &&
        loginData.boutique_id &&
        loginData.boutique_id !== 0
      ) {
        setValueInLocalStorage('boutique_id', loginData.boutique_id);

        const userData = {
          phone_number: phoneNumber,
          extention: dialCode,
          dial_code: dialCode,
          ...loginData,
        };

        setValueInLocalStorage('userDetails', userData);

        dispatch(
          updateUserData({
            data: userData,
          })
        );

        void getBoutiqueDetails();
        navigate('/dashboard');
      } else {
        // Navigate to onboarding if boutique_id is 0, null, or doesn't exist
        // Remove boutique_id from localStorage if it's 0 or doesn't exist to prevent dashboard redirect
        if (loginData?.boutique_id === 0 || !loginData?.boutique_id) {
          setValueInLocalStorage('boutique_id', '');
        }

        const userData = {
          phone_number: phoneNumber,
          extention: dialCode,
          dial_code: dialCode,
          ...(loginData || {}),
        };

        setValueInLocalStorage('userDetails', userData);
        dispatch(updateUserData({ data: userData }));

        navigate('/onboarding');
      }
    } catch (error: any) {
      toasts('error', error?.message ?? 'OTP verification failed', 'verify-otp');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      setIsLoading(true);

      const response = await api.postRequest('api/v1/auth/user/generate_otp', {
        mobile_number: `${dialCode}${phoneNumber}`,
      });

      const { data, status } = response || {};

      if (status && data?.msg === 'otp sent') {
        setOtpTimer(maxOtpTimer);
        setOtp(Array(otpNumCount).fill(''));
      } else {
        toasts('error', data?.msg || 'Failed to resend OTP', 'resend-otp');
      }
    } catch (error: any) {
      toasts('error', error?.message ?? 'Failed to resend OTP', 'resend-otp');
    } finally {
      setIsLoading(false);
    }
  };

  const getBoutiqueDetails = async () => {
    const boutique_id = getValueFromLocalStorage('boutique_id');

    if (!_isNil(boutique_id)) {
      const response = await api.getRequest(`boutique/${boutique_id}`);

      const { data, status } = response;

      if (status) {
        dispatch(updateBoutiqueData({ boutiqueData: data }));
      }
    }
  };

  return (
    <ScreenContainer>
      <LoginContainer>
        <Text size="xxxl" color="var(--color-black)" fontWeight={800}>
          Verify Phone Number
        </Text>

        <Text color="var(--color-black)" className="login-help-text">
          {`Check your SMS messages. We've sent you a PIN at ${dialCode}${phoneNumber}`}
        </Text>

        <OTPBoxContainer>
          <OTPBox className="input-otp">
            {otp.map((val: string, index: number) => (
              <input
                autoFocus={index === 0}
                className={`otp ${!_isEmpty(errMsg) && 'otp-error'}`}
                maxLength={1}
                type="text"
                onChange={handleChange(index)}
                onKeyDown={handleKeyDown(index)}
                ref={addToRefs}
                key={index}
                value={val}
                onFocus={onFocus}
                autoComplete="new-password"
                readOnly
              />
            ))}
          </OTPBox>
          {!_isEmpty(errMsg) && <Text color="tertiary">{errMsg}</Text>}
        </OTPBoxContainer>

        <OTPTimerText>
          <Text color="var(--color-ebonyClay)" fontWeight={600}>
            {`Didn't receive SMS?`}
          </Text>
          <Text color="var(--color-islamicGreen)" fontWeight={600}>
            {`0:${otpTimer} Sec`}
          </Text>
        </OTPTimerText>

        {otpTimer !== 0 && (
          <Button
            className="otp-btn"
            disabled={!_isEmpty(errMsg)}
            trailingIcon={<RightArrowIcon />}
            onClick={handleVerifyOtp}
          >
            Verify OTP
          </Button>
        )}

        {otpTimer === 0 && (
          <Button className="otp-btn" trailingIcon={<RightArrowIcon />} onClick={handleResendOtp}>
            Resend OTP
          </Button>
        )}
      </LoginContainer>
      <Loader showLoader={isLoading} />
    </ScreenContainer>
  );
};

export default OTPScreen;
